import {
  filtersToGraphQLFilters,
  IPageFilterProps,
  IProperty,
  PageFiltersRenderer,
  PerformanceDataExplorerKey,
  performanceWorkFilterOptionsQuery,
  SortModel,
  useDataExplorerReportStore,
  workflowsUtils,
} from '@diana/core';
import { useQuery } from '@tanstack/react-query';
import { omit } from 'lodash';
import { ReactElement, useMemo } from 'react';

export const PerformanceFilterRenderer = ({ filterProps }: { filterProps: IPageFilterProps }) => {
  const {
    filter: { property },
    filtersValues,
  } = filterProps;
  const flows = useDataExplorerReportStore((store) => store.flows);
  const availableFilters = useDataExplorerReportStore((s) => s.availableFilters);
  const properties = useDataExplorerReportStore((s) => s.properties);
  const flowTypes = useMemo(() => workflowsUtils.getUniqueFlowTypes({ flows }), [flows]);

  const filterProperties = filtersToGraphQLFilters({
    filters: availableFilters,
    filtersValues: omit(filtersValues, property.key), // prevent passing filtered values for the the said current filter key
  });

  const optionsQuery = performanceWorkFilterOptionsQuery.get({
    key: property.key,
    displayNamePath: property.displayNamePath,
    flowTypes,
    isDefaultProperty: property.isDefaultProperty,
    valuePaths: property.valuePaths,
    filterType: property.type!,
    flowIds: flows.map((flow) => flow.id),
    flowOriginIds: flows.map((flow) => flow.flowOriginId),
    showCompleted: true,
    showDeleted: true,
    showInProgress: true,
    additionalProperties: properties.map((p) => ({
      key: p.key,
      displayName: p.displayName,
      isExpandedProperty: false,
      valuePath: p.valuePath,
      valuePaths: p.valuePaths,
      displayNamePath: p.displayNamePath ?? null,
    })),
    filterProperties,
  });

  const { data, isFetching } = useQuery({
    queryKey: [...optionsQuery.key, filterProperties],
    queryFn: optionsQuery.fn,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  return <PageFiltersRenderer filterProps={filterProps} data={data} isLoading={isFetching} />;
};

export const createPropertyToFilter = () => {
  return (property: IProperty) => {
    return {
      property,
      renderFilter: (props: IPageFilterProps): ReactElement => {
        return <PerformanceFilterRenderer filterProps={props} />;
      },
    };
  };
};

export const getDataExplorerGraphQLWorksQueryKey = (
  graphQLFilters: string,
  sortModel?: SortModel,
) => [PerformanceDataExplorerKey, graphQLFilters, JSON.stringify(sortModel)];
